// src/components/ui/sections/CityServiceLinks/city-service-links-grid.tsx
'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, ArrowRight, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographySmall } from '@/components/ui/typography';
import { SUPPORTED_CITIES, type ServiceCategory } from '@/app/[city]/services/[category]/[type]/types';
import { SERVICE_AREA_DATA_MAP } from '@/app/about/our-service-areas/data';
import { SERVICE_TYPE_DATA_MAP, RENOVATION_TYPE_DATA_MAP } from '@/app/services/[category]/data';
import { cn } from '@/lib/utils';

const panelVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1 },
};

type CityServiceLinksGridProps =
  | {
      mode: 'by-city';
      category: ServiceCategory;
      type?: never;
      title?: string;
      description?: string;
    }
  | {
      mode: 'by-service-type';
      category: ServiceCategory;
      type: string;
      title?: string;
      description?: string;
    };

function CityServicesPanel({
  citySlug,
  cityName,
  onClose,
}: {
  citySlug: string;
  cityName: string;
  onClose: () => void;
}) {
  // Intentionally shows all types for both categories — this is a city-level
  // service overview, not scoped to the parent CityServiceLinksGrid's category.
  const buildTypes = Object.entries(SERVICE_TYPE_DATA_MAP);
  const renovateTypes = Object.entries(RENOVATION_TYPE_DATA_MAP);

  return (
    <div className="rounded-lg border border-primary/20 bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="font-semibold text-foreground text-lg">
          {cityName}, OK
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Close panel"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Design &amp; Build
          </p>
          {buildTypes.map(([slug, data]) => (
            <Link
              key={slug}
              href={`/${citySlug}/services/design-and-build/${data.urlSlug}`}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronRight className="size-3 shrink-0" />
              {data.name}
            </Link>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Design &amp; Renovate
          </p>
          {renovateTypes.map(([slug, data]) => (
            <Link
              key={slug}
              href={`/${citySlug}/services/design-and-renovate/${data.urlSlug}`}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronRight className="size-3 shrink-0" />
              {data.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CityServiceLinksGrid({
  mode,
  category,
  type,
  title = 'Available in Your City',
  description = 'Cornerstone Homes serves 8 communities across northeastern Oklahoma.',
}: CityServiceLinksGridProps) {
  const [selectedCity, setSelectedCity] = useState<string | null>(null);

  function handleCardClick(slug: string) {
    setSelectedCity((prev) => (prev === slug ? null : slug));
  }

  const activeCityData = selectedCity ? SERVICE_AREA_DATA_MAP[selectedCity] : null;

  return (
    <SectionBase id="city-service-links" background="bg-card">
      <div className="flex flex-col gap-8">
        <div className="text-center">
          <SectionHeader
            subtitle="SERVICE AREAS"
            title={title}
            description={description}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SUPPORTED_CITIES.map((slug) => {
            const cityData = SERVICE_AREA_DATA_MAP[slug];
            if (!cityData) return null;

            if (mode === 'by-city') {
              const isActive = selectedCity === slug;
              return (
                <button
                  key={slug}
                  type="button"
                  aria-expanded={isActive}
                  aria-controls="city-panel"
                  onClick={() => handleCardClick(slug)}
                  className={cn(
                    'group flex flex-col gap-2 p-5 rounded-lg bg-background border transition-colors text-left cursor-pointer',
                    isActive
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary hover:bg-primary/5',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-primary shrink-0" />
                    <span
                      className={cn(
                        'font-semibold transition-colors',
                        isActive ? 'text-primary' : 'text-foreground group-hover:text-primary',
                      )}
                    >
                      {cityData.name}
                    </span>
                    <span className="text-xs text-muted-foreground ml-auto">OK</span>
                  </div>
                  <TypographySmall className="text-muted-foreground leading-snug line-clamp-2">
                    {cityData.tagline}
                  </TypographySmall>
                  <div
                    className={cn(
                      'flex items-center gap-1 text-xs mt-auto pt-1 transition-colors',
                      isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary',
                    )}
                  >
                    <span>{isActive ? 'Hide services' : 'View services'}</span>
                    <ChevronRight
                      className={cn(
                        'size-3 transition-transform',
                        isActive ? 'rotate-90' : 'group-hover:translate-x-0.5',
                      )}
                    />
                  </div>
                </button>
              );
            }

            // by-service-type: original Link behavior unchanged
            const href = `/${slug}/services/${category}/${type}`;
            return (
              <Link
                key={slug}
                href={href}
                className="group flex flex-col gap-2 p-5 rounded-lg bg-background border border-border hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="size-4 text-primary shrink-0" />
                  <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {cityData.name}
                  </span>
                  <span className="text-xs text-muted-foreground ml-auto">OK</span>
                </div>
                <TypographySmall className="text-muted-foreground leading-snug line-clamp-2">
                  {cityData.tagline}
                </TypographySmall>
                <div className="flex items-center gap-1 text-xs text-primary mt-auto pt-1">
                  <span>Learn more</span>
                  <ArrowRight className="size-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>

        <div id="city-panel">
          <AnimatePresence initial={false}>
            {selectedCity !== null && activeCityData ? (
              <motion.div
                key="city-panel"
                variants={panelVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <CityServicesPanel
                  citySlug={selectedCity}
                  cityName={activeCityData.name}
                  onClose={() => setSelectedCity(null)}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </SectionBase>
  );
}
